import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllEmailTemplates, setDefaultEmailTemplate } from '../services/api.service';
import Loading from '../components/loading';
import { FaPlus, FaSearch, FaTimes, FaStar } from 'react-icons/fa';
import Helper from '../services/helper.service';

const langs = ['fr', 'en', 'de'];

const TRIGGER_LABELS = {
    'any': 'Toujours',
    'status.created': 'À la création',
    'status.paid': 'Quand payé',
    'status.in-progress': 'En cours',
    'status.completed': 'Quand complété',
    'status.rejected': 'Quand rejeté',
};

const triggerLabel = (key) =>
    (TRIGGER_LABELS && TRIGGER_LABELS[key]) ||
    key;

const EmailTemplatesListPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [search, setSearch] = useState('');
    const [lang, setLang] = useState('fr');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const run = async () => {
            setLoading(true);
            try {
                const data = await getAllEmailTemplates();
                setTemplates(data || []);
            } catch {
                Helper.toast("Erreur lors du chargement des modèles d'email", "error");
            } finally {
                setLoading(false);
            }
        };
        run();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates;
        const pick = (i18n) => (i18n?.[lang] || i18n?.fr || i18n?.en || i18n?.de || '');

        return templates.filter(t => {
            const trig = triggerLabel(t.trigger || '');
            return (
                pick(t.name).toLowerCase().includes(q) ||
                pick(t.subject).toLowerCase().includes(q) ||
                trig.toLowerCase().includes(q)
            );
        });
    }, [templates, search, lang]);

    const markDefault = async (id) => {
        try {
            setBusy(true);
            await setDefaultEmailTemplate(id);
            const data = await getAllEmailTemplates();
            setTemplates(data || []);
        } catch {
            Helper.toast("Erreur lors de la mise à jour du modèle par défaut", "error");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="max-w-7xl mx-auto p-6">
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-800">Modèles d’email</h1>
                <button
                    className="bg-primary hover:bg-blue-700 text-white px-4 py-2 rounded shadow inline-flex items-center gap-2"
                    onClick={() => navigate('/email-templates/new')}
                >
                    <FaPlus /> Nouveau modèle
                </button>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
                <div className="relative w-full md:max-w-md">
                    <FaSearch className="absolute left-3 top-3 text-gray-500" />
                    <input
                        className="w-full border border-gray-300 rounded px-9 py-2"
                        placeholder="Rechercher un modèle"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-3 text-gray-500"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Langue</span>
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                        className="border rounded px-2 py-1"
                    >
                        {langs.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-24"><Loading /></div>
            ) : filtered.length === 0 ? (
                <p className="text-gray-500">Aucun modèle d'email.</p>
            ) : (
                <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="text-left px-4 py-3">Nom</th>
                                <th className="text-left px-4 py-3">Sujet</th>
                                <th className="text-left px-4 py-3">Déclencheur</th>
                                <th className="text-left px-4 py-3">Statut</th>
                                <th className="text-right px-4 py-3">Par défaut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(t => {
                                const name = t.name?.[lang] || t.name?.fr || t.name?.en || t.name?.de || '(Sans nom)';
                                const subject = t.subject?.[lang] || t.subject?.fr || t.subject?.en || t.subject?.de || '';
                                return (
                                    <tr
                                        key={t._id}
                                        className="border-t hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/email-templates/${t._id}`)}
                                    >
                                        <td className="px-4 py-3 font-medium">{name}</td>
                                        <td className="px-4 py-3">{subject}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-center">
                                                {triggerLabel(t.trigger, lang)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded text-xs ${t.enable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                {t.enable ? 'Activé' : 'Désactivé'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end">
                                                <button
                                                    className="flex items-center gap-2 text-sm group"
                                                    onClick={(e) => { e.stopPropagation(); markDefault(t._id); }}
                                                    disabled={busy}
                                                >
                                                    <FaStar
                                                        className={`w-4 h-4 transition-colors ${t.useByDefault ? 'text-yellow-500' : 'text-gray-400 group-hover:text-yellow-400'}`}
                                                    />
                                                    <span className={t.useByDefault ? 'text-gray-900 font-medium' : 'text-gray-600 group-hover:text-gray-800'}>
                                                        {t.useByDefault ? 'Par défaut' : 'Définir par défaut'}
                                                    </span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default EmailTemplatesListPage;